import type { Tag } from '@epasu/core';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import type { ContactRow } from '@/lib/crm-client';
import { ZUSTAND_LABEL, type Zustand } from './lead-zustand';

/** Farbe je Zustand — der Trichter von grau (noch nichts passiert) bis warm (reif). */
const ZUSTAND_VARIANTE: Record<Zustand, NonNullable<BadgeProps['variant']>> = {
  noch_nicht_angeschrieben: 'neutral',
  in_kampagne_offen: 'info',
  erstansprache_versendet: 'info',
  follow_up_faellig: 'warning',
  antwort_offen: 'success',
  kalt: 'neutral',
  qualifiziert: 'brand',
};

interface Props {
  contact: ContactRow;
  audienceLabel: string;
  /** `undefined`, solange die Zusatzinfo noch lädt. */
  tags: Tag[] | undefined;
  listNames: string[] | undefined;
  zustand: Zustand | undefined;
  selected: boolean;
  onToggle: () => void;
  onOpen: () => void;
}

/** Eine Zeile der Leads-Tabelle — eigene Komponente, weil `LeadsPage.tsx` sonst zu lang würde. */
export function LeadRow({
  contact,
  audienceLabel,
  tags,
  listNames,
  zustand,
  selected,
  onToggle,
  onOpen,
}: Props) {
  return (
    <tr
      className="cursor-pointer border-b last:border-0 hover:bg-muted/40"
      onClick={onOpen}
    >
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={selected}
          onChange={onToggle}
          aria-label={`${contact.displayName} auswählen`}
        />
      </td>
      <td className="px-4 py-3 font-medium">
        {contact.displayName}
        {contact.optedOut && (
          <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-normal text-amber-900">
            abgemeldet
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-muted-foreground">{contact.organizationName ?? '—'}</td>
      <td className="px-4 py-3 text-muted-foreground">{audienceLabel}</td>
      <td className="px-4 py-3">
        {tags === undefined ? (
          <span className="text-muted-foreground">…</span>
        ) : tags.length === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {tags.map((t) => (
              <Badge key={t.id} variant="neutral">
                {t.label}
              </Badge>
            ))}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-muted-foreground">
        {listNames === undefined ? '…' : listNames.length === 0 ? '—' : listNames.join(', ')}
      </td>
      <td className="px-4 py-3">
        {zustand === undefined ? (
          <span className="text-muted-foreground">…</span>
        ) : (
          <Badge variant={ZUSTAND_VARIANTE[zustand]}>{ZUSTAND_LABEL[zustand]}</Badge>
        )}
      </td>
    </tr>
  );
}
